import React from 'react';
import { Button } from 'antd';
import { buttonStyles } from '../../../theme/theme';
import CustomTooltip from '../Modals/CustomTooltip';
import styles from './buttons.module.css';

// Bottone primario standard
export const PrimaryButton = ({ children, icon, className, ...props }) => (
    <Button
        className={`${styles['button-enhance']} ${className || ''}`}
        type="primary"
        icon={icon}
        {...props}
    >
        {children}
    </Button>
);

// Bottone outline
export const OutlineButton = ({ children, icon, className, ...props }) => (
    <Button
        className={`${styles['button-outline']} ${className || ''}`}
        style={buttonStyles.outline}
        icon={icon}
        {...props}
    >
        {children}
    </Button>
);

// Bottone secondario
export const SecondaryButton = ({ children, icon, className, ...props }) => (
    <Button
        className={`${styles['button-enhance']} ${className || ''}`}
        style={buttonStyles.secondary}
        icon={icon}
        {...props}
    >
        {children}
    </Button>
);

// Bottone accent
export const AccentButton = ({ children, icon, className, ...props }) => (
    <Button
        className={`${styles['button-enhance']} ${className || ''}`}
        style={buttonStyles.accent}
        icon={icon}
        {...props}
    >
        {children}
    </Button>
);

// Bottoni di stato
export const SuccessButton = ({ children, icon, className, ...props }) => (
    <Button
        className={`${styles['button-success']} ${styles['button-enhance']} ${className || ''}`}
        style={buttonStyles.success}
        icon={icon}
        {...props}
    >
        {children}
    </Button>
);

export const InfoButton = ({ children, icon, className, ...props }) => (
    <Button
        className={`${styles['button-info']} ${styles['button-enhance']} ${className || ''}`}
        style={buttonStyles.info}
        icon={icon}
        {...props}
    >
        {children}
    </Button>
);

export const WarningButton = ({ children, icon, className, ...props }) => (
    <Button
        className={`${styles['button-warning']} ${styles['button-enhance']} ${className || ''}`}
        style={buttonStyles.warning}
        icon={icon}
        {...props}
    >
        {children}
    </Button>
);

export const ErrorButton = ({ children, icon, className, ...props }) => (
    <Button
        className={`${styles['button-error']} ${styles['button-enhance']} ${className || ''}`}
        style={buttonStyles.error}
        icon={icon}
        {...props}
    >
        {children}
    </Button>
);

// Bottone link
export const LinkButton = ({ children, danger = false, className, ...props }) => (
    <Button
        className={`${styles['button-link']} ${className || ''}`}
        type="link"
        danger={danger}
        {...props}
    >
        {children}
    </Button>
);

// Bottone ghost
export const GhostButton = ({ children, icon, type = "primary", className, ...props }) => (
    <Button
        className={`${styles['button-ghost']} ${className || ''}`}
        type={type}
        ghost
        icon={icon}
        {...props}
    >
        {children}
    </Button>
);

// Bottone con icona rotondo
export const IconButton = ({ icon, tooltip, type, style = {}, className, ...props }) => {
    const button = (
        <Button
            className={`${styles['button-icon']} ${className || ''}`}
            type={type}
            shape="circle"
            icon={icon}
            style={style}
            {...props}
        />
    );

    return tooltip ? (
        <CustomTooltip title={tooltip}>
            {button}
        </CustomTooltip>
    ) : button;
};

// Bottone a larghezza intera
export const BlockButton = ({ children, className, type = "primary", style = {}, ...props }) => (
    <Button
        className={`${styles['button-enhance']} ${className || ''}`}
        type={type}
        block
        style={style}
        {...props}
    >
        {children}
    </Button>
);

// Esporta una funzione di utility per generare stili personalizzati di bottoni
export const createStyledButton = (baseStyle, hoverEffect) => {
    const styleId = `custom-button-${Math.random().toString(36).substring(2, 7)}`;

    // Rimuovi le proprietà transform dall'oggetto hoverEffect
    const cleanHoverEffect = { ...hoverEffect };
    delete cleanHoverEffect.transform;

    const injectStyles = () => {
        // Rimuovi eventuali stili già esistenti con lo stesso ID
        const existingStyle = document.getElementById(styleId);
        if (existingStyle) {
            existingStyle.remove();
        }

        // Crea un nuovo elemento style
        const styleElement = document.createElement('style');
        styleElement.id = styleId;

        // Genera il CSS con variabili standardizzate
        const css = `
            .${styleId} {
                transition: all 0.3s ease !important;
                ${Object.entries(baseStyle).map(([key, value]) => `${key}: ${value};`).join('\n')}
            }
            
            .${styleId}:hover {
                ${Object.entries(cleanHoverEffect).map(([key, value]) => `${key}: ${value};`).join('\n')}
            }
        `;

        styleElement.innerHTML = css;
        document.head.appendChild(styleElement);
    };

    // Componente che gestisce l'inserimento/rimozione degli stili
    const StyledButton = ({ children, ...props }) => {
        React.useEffect(() => {
            injectStyles();
            return () => {
                const styleElement = document.getElementById(styleId);
                if (styleElement) {
                    styleElement.remove();
                }
            };
        }, []);

        return (
            <Button className={styleId} {...props}>
                {children}
            </Button>
        );
    };

    return StyledButton;
}; 